import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  Heading,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContactPersonDto,
  ContactSubjectType,
  EmailInviteLanguage,
  MinimalOrgDto,
  useAddEmailInviteToPrivateSourcingEventMutation,
  useGetOrganizationEntryQuery,
} from "../../../autogen/bff-api";
import { ContactPersonForm } from "../../../common/ContactPersonForm";
import { useApiError } from "../../../common/errors/useApiError";
import { ModalSkeleton } from "../../../common/ModalSkeleton";

interface Props {
  eventId: string;
  minimalOrgEntry?: MinimalOrgDto;
  onClose: () => void;
}

export const V2InviteOrganizationEntryModal = ({ eventId, minimalOrgEntry, onClose }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [addEmailInvite] = useAddEmailInviteToPrivateSourcingEventMutation();

  const { data: organizationEntry, isLoading } = useGetOrganizationEntryQuery(
    minimalOrgEntry ? { entryId: minimalOrgEntry.id } : skipToken,
  );

  const [selectedContactPersons, setSelectedContactPersons] = useState<ContactPersonDto[]>([]);

  const handleClose = () => {
    setSelectedContactPersons([]);
    onClose();
  };

  const createInvites = async () => {
    if (!minimalOrgEntry) throw new Error("Organization entry was unexpectedly undefined");
    try {
      for (const person of selectedContactPersons) {
        await addEmailInvite({
          eventId,
          addEmailInviteRequest: {
            email: person.email,
            language: person.language ?? EmailInviteLanguage.No,
            organizationEntryId: minimalOrgEntry.id,
          },
        }).unwrap();
      }
      handleClose();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Modal isOpen={!!minimalOrgEntry} onClose={handleClose} size="3xl">
      <ModalOverlay />
      <ModalContent width="full" height="60%" minHeight="60%" maxHeight="80%" padding="5" overflow="hidden">
        <ModalCloseButton />
        <Flex minHeight="full" height="full" flexDirection="column" overflow="hidden">
          <Heading as="h2" size="md" pb="2" pr="5">
            {t("Select persons to invite for")} {minimalOrgEntry?.name}
          </Heading>
          {isLoading && <ModalSkeleton />}
          {organizationEntry && (
            <Flex flexDirection="column" grow="1" justifyContent="space-between" overflowY="auto">
              <Flex flexDirection="column">
                <Box pt="5" pl="2">
                  <CheckboxGroup colorScheme="teal">
                    {organizationEntry.contactPersons?.map((p) => (
                      <Checkbox
                        key={p.email}
                        width="full"
                        pb="2"
                        isChecked={!!selectedContactPersons.find((selected) => selected.email === p.email)}
                        onChange={(e) => {
                          setSelectedContactPersons((prev) => {
                            if (e.target.checked)
                              return [...prev.filter((s) => s.email !== p.email), p];
                            return prev.filter((s) => s.email !== p.email);
                          });
                        }}
                      >
                        {`${p.name} (${p.email})`}
                      </Checkbox>
                    ))}
                  </CheckboxGroup>
                </Box>
                <ContactPersonForm
                  subject={{ subjectType: ContactSubjectType.OrganizationEntry, subjectId: organizationEntry.id }}
                  onSubmit={(newContactPerson) =>
                    setSelectedContactPersons((prev) => [...prev, newContactPerson])
                  }
                />
              </Flex>
              <FormControl width="full" pt="5">
                <Button isDisabled={!selectedContactPersons.length} onClick={createInvites}>
                  {t("Create invite")}
                </Button>
              </FormControl>
            </Flex>
          )}
        </Flex>
      </ModalContent>
    </Modal>
  );
};
